// @flow

import { observer } from "mobx-react";
import React from "react";
import TextExtractor from "app/util/TextExtractor";

import TimelineItem from "app/models/TimelineItem";
import type {
    TimelineItemMetadata,
    TimelineItemMetaImage
} from "app/models/TimelineItem";

import HtmlVideo from "app/components/HtmlVideo/HtmlVideo";
import HtmlAudio from "app/components/HtmlAudio/HtmlAudio";
import FilePreview from "app/components/FilePreview/FilePreview";
import AnimatedIconLoading from "app/components/AnimatedIconLoading/AnimatedIconLoading";

import ImageHelper from "app/util/ImageHelper";
import ObjectHelper from "app/util/ObjectHelper";

import "./WizardTimelineCardPreview.scss";

type Props = {
    metadata?: TimelineItemMetadata,
    url?: string,
    image?: string,
    file?: File,
    loading: boolean,
    onRemove: Function
};

@observer
export default class WizardTimelineCardPreview extends React.Component<Props> {
    static getType(
        loading: boolean,
        metadata?: TimelineItemMetadata,
        image?: string,
        file?: File
    ) {
        if (loading) return "loading";
        if (file) return "file_object";
        if (image) return "uploaded_file";
        if (metadata) return "url";

        return null;
    }

    getType() {
        return this.constructor.getType(
            this.props.loading,
            this.props.metadata,
            this.props.image,
            this.props.file
        );
    }

    render() {
        const type = this.getType();
        if (!type) return null;

        let renderByType = {
            loading: this.renderLoading.bind(this),
            uploaded_file: this.renderUploadedFile.bind(this),
            url: this.renderUrl.bind(this),
            file_object: this.renderFileObject.bind(this)
        }[type];

        const classes = [
            "WizardTimelineCardPreview",
            "WizardTimelineCardPreview--" + type
        ];

        return (
            <div className={classes.join(" ")}>
                {type === "loading" ? null : (
                    <button
                        type="button"
                        className="WizardTimelineCardPreview__remove"
                        onClick={this.props.onRemove}
                    >
                        <i className="fa fa-remove" />
                        <span>Remove</span>
                    </button>
                )}
                {renderByType()}
            </div>
        );
    }

    renderLoading() {
        return (
            <div className="animated fadeInDown">
                <AnimatedIconLoading /> Loading Preview
            </div>
        );
    }

    renderUploadedFile() {
        const image = this.props.image;
        if (!image) return null;

        if (this.props.metadata && this.props.metadata.video) {
            //Transcoded videos
            let videoUrls = ObjectHelper.mapValues(
                this.props.metadata.video,
                url => ImageHelper.makeUrl(url)
            );
            return <HtmlVideo videoUrls={videoUrls} />;
        }
        if (image.indexOf(".webm") !== -1) {
            let videoUrls = { "video/webm": ImageHelper.makeUrl(image) };
            return <HtmlVideo videoUrls={videoUrls} />;
        }
        if (image.indexOf(".mp4") !== -1) {
            let videoUrls = { "video/mp4": ImageHelper.makeUrl(image) };
            return <HtmlVideo videoUrls={videoUrls} />;
        }
        if (
            image.indexOf(".mpga") !== -1 ||
            image.indexOf(".mp3") !== -1 ||
            image.indexOf(".wav") !== -1
        )
            return <HtmlAudio src={ImageHelper.makeUrl(image)} />;

        return (
            <img src={ImageHelper.makeUrl(image)} alt="Timeline item preview" />
        );
    }

    renderUrl() {
        const url = this.props.url;
        const metadata = this.props.metadata;
        if (!url || !metadata) return null;

        const domain = TextExtractor.domain(url);
        const metaImage = TimelineItem.metaImage(metadata.image, domain);
        const src = this.grabImage(metaImage);

        return (
            <a href={url} target="_blank">
                <div
                    className="WizardTimelineCardPreview--url__image"
                    style={{ backgroundImage: `url('${src}')` }}
                    alt="Timeline item preview"
                />

                <div className="WizardTimelineCardPreview--url__title">
                    {metadata.title}
                </div>

                <div className="WizardTimelineCardPreview--url__description">
                    {metadata.description}
                </div>

                <div className="WizardTimelineCardPreview--url__domain">
                    {domain}
                </div>
            </a>
        );
    }

    renderFileObject() {
        if (!this.props.file) return null;
        return <FilePreview file={this.props.file} />;
    }

    grabImage(metaImage: ?TimelineItemMetaImage): string {
        if (!metaImage) return "";

        return ImageHelper.makeUrl(metaImage.src, "jpg", { width: 200 });
    }
}



// WEBPACK FOOTER //
// ./src/app/components/TimelineCard/WizardTimelineCard/WizardTimelineCardPreview/WizardTimelineCardPreview.js